import React, { useState } from "react";
import {
  Building2,
  PlusCircle,
  Clock,
  MailCheck,
  ChevronRight,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  ShieldAlert,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { organizationApi } from "@/api/organization/organizationApi";
import { toast } from "sonner";

const MOCK_EXISTING_PROPERTIES = [
  { id: "org-8041", name: "Grand Palace Hotel & Spa", type: "Hotel / PMS", code: "ORG-8041", owner: "owner@grandpalace.com" },
  { id: "org-3192", name: "Annapurna Mountain Lodge", type: "Resort", code: "ORG-3192", owner: "admin@annapurnamtn.com" },
  { id: "org-5510", name: "Pokhara Heights Retreat", type: "Boutique Stay", code: "ORG-5510", owner: "ops@pokharaheights.com" },
  { id: "org-2094", name: "Everest View Suites", type: "Hotel / PMS", code: "ORG-2094", owner: "info@everestview.com" },
  { id: "org-7712", name: "FireWood Resort & Spa", type: "Resort", code: "ORG-7712", owner: "contact@firewoodresort.com" },
];

export const PENDING_REQUESTS_KEY = "hotel_pending_property_requests";

export const getStoredPendingRequests = () => {
  try {
    const raw = localStorage.getItem(PENDING_REQUESTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

export const savePendingRequest = (requestItem) => {
  try {
    const current = getStoredPendingRequests();
    const updated = [requestItem, ...current.filter((x) => x.id !== requestItem.id)];
    localStorage.setItem(PENDING_REQUESTS_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent("hotel:pendingRequestsUpdated"));
  } catch (e) {
    console.error("Failed to save pending property request", e);
  }
};

export default function AddPropertyModal({ open, onOpenChange, onRequestSubmitted }) {
  const [mode, setMode] = useState("select"); // "select" | "register_new" | "request_existing" | "registered_success" | "requested_success"
  const [submitting, setSubmitting] = useState(false);

  // Form State: Register New
  const [newProp, setNewProp] = useState({
    name: "",
    type: "Hotel / PMS",
    address: "",
    contactEmail: "",
    contactNumber: "",
    description: "",
  });

  // Form State: Request Existing
  const [requestProp, setRequestProp] = useState({
    selectedOrgId: "",
    requestedRole: "Front Desk Staff",
    message: "",
  });

  const resetForm = () => {
    setMode("select");
    setSubmitting(false);
    setNewProp({
      name: "",
      type: "Hotel / PMS",
      address: "",
      contactEmail: "",
      contactNumber: "",
      description: "",
    });
    setRequestProp({
      selectedOrgId: "",
      requestedRole: "Front Desk Staff",
      message: "",
    });
  };

  const handleClose = (nextOpen) => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  const handleRegisterNewSubmit = async (e) => {
    e.preventDefault();
    if (!newProp.name.trim()) {
      toast.error("Please enter a property name.");
      return;
    }

    setSubmitting(true);
    try {
      await organizationApi.submitPropertyRegistration(newProp);

      const requestItem = {
        id: `pending-new-${Date.now()}`,
        organizationId: `pending-${Date.now()}`,
        organizationName: newProp.name.trim(),
        organizationType: newProp.type,
        status: "pending_superadmin",
        badgeText: "⏳ Pending SuperAdmin Approval",
        submittedAt: new Date().toISOString(),
        details: { ...newProp },
      };

      savePendingRequest(requestItem);
      toast.success("Property registration request submitted to SuperAdmin!");
      setMode("registered_success");
      onRequestSubmitted?.(requestItem);
    } catch (err) {
      toast.error("Failed to submit property registration. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestExistingSubmit = async (e) => {
    e.preventDefault();
    if (!requestProp.selectedOrgId) {
      toast.error("Please select an existing property.");
      return;
    }

    const selectedProp = MOCK_EXISTING_PROPERTIES.find(
      (p) => p.id === requestProp.selectedOrgId
    );

    setSubmitting(true);
    try {
      await organizationApi.requestPropertyAccess({
        organizationId: requestProp.selectedOrgId,
        requestedRole: requestProp.requestedRole,
        message: requestProp.message,
      });

      const requestItem = {
        id: `pending-access-${Date.now()}`,
        organizationId: requestProp.selectedOrgId,
        organizationName: selectedProp?.name || "Existing Property",
        organizationType: selectedProp?.type || "Hotel / PMS",
        organizationCode: selectedProp?.code || "",
        status: "pending_owner",
        badgeText: "✉️ Access Request Sent",
        submittedAt: new Date().toISOString(),
        details: { ...requestProp, ownerEmail: selectedProp?.owner },
      };

      savePendingRequest(requestItem);
      toast.success("Access request sent to property owner!");
      setMode("requested_success");
      onRequestSubmitted?.(requestItem);
    } catch (err) {
      toast.error("Failed to send access request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg overflow-hidden p-0 sm:rounded-2xl">
        {/* Header */}
        <div className="border-b bg-muted/30 px-6 py-5">
          <div className="flex items-center gap-3">
            {mode !== "select" && !mode.endsWith("_success") && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setMode("select")}
                className="h-8 w-8 rounded-full"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div>
              <DialogTitle className="text-lg font-semibold tracking-tight">
                {mode === "select" && "Add or Connect Property"}
                {mode === "register_new" && "Register New Property"}
                {mode === "request_existing" && "Request Access to Property"}
                {mode === "registered_success" && "Registration Submitted"}
                {mode === "requested_success" && "Access Request Sent"}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                {mode === "select" &&
                  "Register a new stay or request access to an existing property."}
                {mode === "register_new" &&
                  "Submit property details for SuperAdmin review & verification."}
                {mode === "request_existing" &&
                  "Request staff or manager access from an existing property owner."}
                {mode === "registered_success" &&
                  "Your property registration is under review."}
                {mode === "requested_success" &&
                  "Your request has been delivered to the property owner."}
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* STEP 1: CHOICE SELECTION */}
          {mode === "select" && (
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => setMode("register_new")}
                className="group flex items-start gap-4 rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-all hover:border-emerald-500/50 hover:bg-emerald-500/5 hover:shadow-md"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
                  <PlusCircle className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground text-sm">
                      Register a New Property
                    </span>
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                      New Stay
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Set up a new hotel, resort, or restaurant. Goes to SuperAdmin for verification & activation.
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-emerald-600" />
              </button>

              <button
                type="button"
                onClick={() => setMode("request_existing")}
                className="group flex items-start gap-4 rounded-xl border border-border bg-card p-4 text-left shadow-sm transition-all hover:border-blue-500/50 hover:bg-blue-500/5 hover:shadow-md"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400">
                  <Building2 className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground text-sm">
                      Request Access to Existing Property
                    </span>
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800 dark:bg-blue-950 dark:text-blue-300">
                      Link Account
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    Select an existing registered property and send a staff/manager access request to the property owner.
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-blue-600" />
              </button>
            </div>
          )}

          {/* STEP 2A: REGISTER NEW PROPERTY FORM */}
          {mode === "register_new" && (
            <form onSubmit={handleRegisterNewSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="prop-name" className="text-xs font-semibold">
                  Property / Hotel Name *
                </Label>
                <Input
                  id="prop-name"
                  placeholder="e.g. Himalayan Sunrise Resort & Spa"
                  value={newProp.name}
                  onChange={(e) => setNewProp({ ...newProp, name: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="prop-type" className="text-xs font-semibold">
                    Property Type
                  </Label>
                  <Select
                    value={newProp.type}
                    onValueChange={(val) => setNewProp({ ...newProp, type: val })}
                  >
                    <SelectTrigger id="prop-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hotel / PMS">Hotel / PMS</SelectItem>
                      <SelectItem value="Resort">Resort & Spa</SelectItem>
                      <SelectItem value="Food & Beverages">Food & Beverages</SelectItem>
                      <SelectItem value="Boutique Stay">Boutique Stay</SelectItem>
                      <SelectItem value="Apartment">Apartment / Villa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="prop-phone" className="text-xs font-semibold">
                    Contact Phone
                  </Label>
                  <Input
                    id="prop-phone"
                    placeholder="+977 9801234567"
                    value={newProp.contactNumber}
                    onChange={(e) => setNewProp({ ...newProp, contactNumber: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="prop-address" className="text-xs font-semibold">
                  Location / Address
                </Label>
                <Input
                  id="prop-address"
                  placeholder="e.g. Lakeside Ward 6, Pokhara, Nepal"
                  value={newProp.address}
                  onChange={(e) => setNewProp({ ...newProp, address: e.target.value })}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="prop-email" className="text-xs font-semibold">
                  Official / Owner Email
                </Label>
                <Input
                  id="prop-email"
                  type="email"
                  placeholder="owner@himalayansunrise.com"
                  value={newProp.contactEmail}
                  onChange={(e) => setNewProp({ ...newProp, contactEmail: e.target.value })}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="prop-desc" className="text-xs font-semibold">
                  Operational Description / Notes
                </Label>
                <Textarea
                  id="prop-desc"
                  rows={2}
                  placeholder="Brief summary of room capacity, amenities, or special requests..."
                  value={newProp.description}
                  onChange={(e) => setNewProp({ ...newProp, description: e.target.value })}
                />
              </div>

              <div className="rounded-lg bg-amber-50 border border-amber-200/80 p-3 text-xs text-amber-900 flex items-start gap-2.5">
                <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                <span>
                  After submitting, your property will be placed on <strong>Pending SuperAdmin Approval</strong>. You can monitor the status anytime in your Organizations dropdown.
                </span>
              </div>

              <DialogFooter className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMode("select")}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Registration"}
                </Button>
              </DialogFooter>
            </form>
          )}

          {/* STEP 2B: REQUEST EXISTING PROPERTY FORM */}
          {mode === "request_existing" && (
            <form onSubmit={handleRequestExistingSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="select-existing-org" className="text-xs font-semibold">
                  Select Existing Property *
                </Label>
                <Select
                  value={requestProp.selectedOrgId}
                  onValueChange={(val) => setRequestProp({ ...requestProp, selectedOrgId: val })}
                >
                  <SelectTrigger id="select-existing-org">
                    <SelectValue placeholder="Choose a registered property..." />
                  </SelectTrigger>
                  <SelectContent>
                    {MOCK_EXISTING_PROPERTIES.map((prop) => (
                      <SelectItem key={prop.id} value={prop.id}>
                        {prop.name} ({prop.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="requested-role" className="text-xs font-semibold">
                  Requested Staff / Access Role
                </Label>
                <Select
                  value={requestProp.requestedRole}
                  onValueChange={(val) => setRequestProp({ ...requestProp, requestedRole: val })}
                >
                  <SelectTrigger id="requested-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Front Desk Staff">Front Desk Staff</SelectItem>
                    <SelectItem value="Duty Manager">Duty Manager</SelectItem>
                    <SelectItem value="Operations Lead">Operations Lead</SelectItem>
                    <SelectItem value="General Manager">General Manager</SelectItem>
                    <SelectItem value="POS Operator">POS Operator</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="request-message" className="text-xs font-semibold">
                  Message / Note to Property Owner
                </Label>
                <Textarea
                  id="request-message"
                  rows={3}
                  placeholder="Explain why you are requesting access to this property..."
                  value={requestProp.message}
                  onChange={(e) => setRequestProp({ ...requestProp, message: e.target.value })}
                />
              </div>

              <div className="rounded-lg bg-blue-50 border border-blue-200/80 p-3 text-xs text-blue-900 flex items-start gap-2.5">
                <MailCheck className="h-4 w-4 shrink-0 text-blue-600 mt-0.5" />
                <span>
                  Your request will be delivered to the property owner. Access will automatically activate as soon as the owner approves.
                </span>
              </div>

              <DialogFooter className="mt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setMode("select")}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={submitting}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Access Request"}
                </Button>
              </DialogFooter>
            </form>
          )}

          {/* SUCCESS VIEW: REGISTERED NEW */}
          {mode === "registered_success" && (
            <div className="flex flex-col items-center justify-center py-4 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <Clock className="h-7 w-7" />
              </div>
              <h3 className="mt-3 text-base font-semibold">Registration Pending Approval</h3>
              <p className="mt-1 text-xs text-muted-foreground max-w-sm">
                Your property <strong>{newProp.name}</strong> has been submitted to the SuperAdmin systems for review.
              </p>
              <div className="mt-4 rounded-xl border bg-amber-50/50 p-3 text-left w-full text-xs space-y-1">
                <p className="font-semibold text-amber-900">Current Status: Pending SuperAdmin Approval</p>
                <p className="text-amber-800">You can monitor this property status anytime in your Organizations dropdown list.</p>
              </div>
              <Button
                type="button"
                className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleClose(false)}
              >
                Done
              </Button>
            </div>
          )}

          {/* SUCCESS VIEW: REQUESTED EXISTING */}
          {mode === "requested_success" && (
            <div className="flex flex-col items-center justify-center py-4 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h3 className="mt-3 text-base font-semibold">Access Request Sent</h3>
              <p className="mt-1 text-xs text-muted-foreground max-w-sm">
                Your staff access request has been sent to the property owner.
              </p>
              <div className="mt-4 rounded-xl border bg-blue-50/50 p-3 text-left w-full text-xs space-y-1">
                <p className="font-semibold text-blue-900">Current Status: Access Request Sent</p>
                <p className="text-blue-800">You will gain access automatically once the property owner approves your request.</p>
              </div>
              <Button
                type="button"
                className="mt-5 w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => handleClose(false)}
              >
                Done
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
